/**
 * This feature is used to manage browser Cookies
 * 
 * @class Cookies
 * @param {Object} [config]
 * @param {number} [config.expires]
 * @param {string} [config.domain]
 * @param {string} [config.path]
 * @param {boolean} [config.secure]
 */

export default class Cookies {
    constructor (config = {}) {
        this.config = {
            expires: null,
            domain: null,
            path: '/',
            secure: false,
            ...config
        };

        this.cookies = {};
    }

    set (name, data, options = {}) {
        if (typeof name !== 'string') {
            throw new Error('Cookie name must be a string');
        }
        if (typeof data !== 'object' || data === null) {
            throw new Error('Cookie data must be an object');
        }

        const cookieConfig = { ...this.config, ...options };

        const cookieValue = JSON.stringify(data);

        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(cookieValue)}`;

        if (cookieConfig.expires) {
            cookie += `;expires=${this._formatExpires(cookieConfig.expires)}`;
        }

        if (cookieConfig.domain) {
            cookie += `;domain=${cookieConfig.domain}`;
        }

        if (cookieConfig.path) {
            cookie += `;path=${cookieConfig.path}`;
        }

        if (cookieConfig.secure) {
            cookie += `;secure`;
        }

        document.cookie = cookie;

        this.cookies[name] = { data, config: cookieConfig };
    }

    get (name) {
        if (typeof name !== 'string') {
            throw new Error('Cookie name must be a string');
        }

        const cookieStr = document.cookie
            .split('; ')
            .find(row => row.startsWith(`${encodeURIComponent(name)}=`));

        if (!cookieStr) return null;

        const value = decodeURIComponent(cookieStr.split('=')[1]);

        try {
            return JSON.parse(value);
        } catch (error) {
            return value;
        }
    }

    setProperty (name, path, value, options = {}) {
        if (typeof name !== 'string' || typeof path !== 'string') {
            throw new Error('Cookie name and path must be strings');
        }

        const data = this.get(name) || {};
        const keys = path.split('/');

        let current = data;

        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
                current[keys[i]] = {};
            }

            current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = value;

        this.set(name, data, options);
    }

    getProperty (name, path) {
        if (typeof name !== 'string' || typeof path !== 'string') {
            throw new Error('Cookie name and path must be strings');
        }

        const data = this.get(name);
        if (!data) return null;

        const keys = path.split('/');

        let current = data;

        for (let i = 0; i < keys.length; i++) {
            if (!current[keys[i]]) return null;
    
            current = current[keys[i]];
        }

        return current;
    }

    delete (name, options = {}) {
        if (typeof name !== 'string') {
            throw new Error('Cookie name must be a string');
        }

        this.set(name, {}, { ...options, expires: -1 });

        delete this.cookies[name];
    }

    _formatExpires (expires) {
        if (typeof expires === 'number') {
            return new Date(Date.now() + expires * 1000).toUTCString();
        }

        if (expires instanceof Date) {
            return expires.toUTCString();
        }

        throw new Error('Expires must be a number or a Date');
    }
}
